import "./CompanyPurchaseOrders.css";
import { useCallback, useEffect, useMemo, useState } from "react";

import axios from "axios";
import { toast } from "react-toastify";
import { usePermissions } from "./PermissionContext";

const API_URL = "/api";

const headers = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem(
      "company_token"
    )}`,
  },
});

const today = () => new Date().toISOString().slice(0, 10);

const emptyOrder = () => ({
  supplier_id: "",
  order_date: today(),
  expected_date: "",
  notes: "",
});

const formatQuantity = (value) =>
  new Intl.NumberFormat("es-CO", {
    maximumFractionDigits: 3,
  }).format(Number(value || 0));

const formatDate = (value) =>
  value
    ? new Date(`${value}T00:00:00`).toLocaleDateString("es-CO")
    : "—";

function CompanyPurchaseOrders() {
  const { can } = usePermissions();

  const canManage = can("purchases.create");

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);

  const [orderForm, setOrderForm] = useState(emptyOrder);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [receiptRows, setReceiptRows] = useState([]);
  const [receiptDate, setReceiptDate] = useState(today());
  const [receiptNotes, setReceiptNotes] = useState("");
  const [savingReceipt, setSavingReceipt] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [suppliersResponse, productsResponse, ordersResponse] =
        await Promise.all([
          axios.get(`${API_URL}/suppliers/me`, {
            ...headers(),
            params: { active_only: true },
          }),
          axios.get(`${API_URL}/products/me`, headers()),
          axios.get(
            `${API_URL}/purchase-orders/me`,
            headers()
          ),
        ]);

      setSuppliers(suppliersResponse.data);
      setProducts(
        productsResponse.data.filter(
          (product) => product.is_active
        )
      );
      setOrders(ordersResponse.data);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "No fue posible cargar las órdenes de compra."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const addProduct = () => {
    const product = products.find(
      (item) => item.id === Number(selectedProduct)
    );

    if (!product) {
      toast.error("Selecciona un producto.");
      return;
    }

    if (items.some((item) => item.product_id === product.id)) {
      toast.error("El producto ya fue agregado.");
      return;
    }

    setItems((current) => [
      ...current,
      {
        product_id: product.id,
        sku: product.sku,
        name: product.name,
        requested_quantity: 1,
        estimated_unit_cost: Number(
          product.purchase_price || 0
        ),
      },
    ]);

    setSelectedProduct("");
  };

  const updateItem = (productId, field, value) => {
    setItems((current) =>
      current.map((item) =>
        item.product_id === productId
          ? { ...item, [field]: Number(value) }
          : item
      )
    );
  };

  const removeItem = (productId) => {
    setItems((current) =>
      current.filter((item) => item.product_id !== productId)
    );
  };

  const createOrder = async (event) => {
    event.preventDefault();

    if (!orderForm.supplier_id || items.length === 0) {
      toast.error(
        "Selecciona un proveedor y agrega al menos un producto."
      );
      return;
    }

    if (
      items.some(
        (item) =>
          item.requested_quantity <= 0 ||
          item.estimated_unit_cost < 0
      )
    ) {
      toast.error("Revisa las cantidades y costos.");
      return;
    }

    setSaving(true);

    try {
      await axios.post(
        `${API_URL}/purchase-orders/me`,
        {
          supplier_id: Number(orderForm.supplier_id),
          order_date: orderForm.order_date,
          expected_date: orderForm.expected_date || null,
          notes: orderForm.notes || null,
          items: items.map((item) => ({
            product_id: item.product_id,
            requested_quantity: item.requested_quantity,
            estimated_unit_cost: item.estimated_unit_cost,
          })),
        },
        headers()
      );

      toast.success("Orden de compra creada en borrador.");
      setOrderForm(emptyOrder());
      setItems([]);
      await loadData();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "No fue posible crear la orden."
      );
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (order, status) => {
    try {
      await axios.patch(
        `${API_URL}/purchase-orders/me/${order.id}/status`,
        { status },
        headers()
      );

      toast.success(
        status === "sent"
          ? "Orden enviada al proveedor."
          : "Orden cancelada."
      );

      await loadData();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "No fue posible actualizar la orden."
      );
    }
  };

  const openReceipt = async (order) => {
    try {
      const response = await axios.get(
        `${API_URL}/purchase-orders/me/${order.id}`,
        headers()
      );

      const detail = response.data;

      setSelectedOrder(detail);
      setReceiptDate(today());
      setReceiptNotes("");

      setReceiptRows(
        detail.items
          .filter(
            (item) => Number(item.pending_quantity) > 0
          )
          .map((item) => ({
            purchase_order_item_id: item.id,
            product_name: item.product_name,
            pending_quantity: Number(item.pending_quantity),
            quantity: 0,
            unit_cost: Number(item.estimated_unit_cost || 0),
            lot_number: "",
            expiration_date: "",
          }))
      );
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "No fue posible cargar la orden."
      );
    }
  };

  const updateReceiptRow = (index, field, value) => {
    setReceiptRows((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [field]:
                field === "lot_number" ||
                field === "expiration_date"
                  ? value
                  : Number(value),
            }
          : item
      )
    );
  };

  const registerReceipt = async (event) => {
    event.preventDefault();

    if (!selectedOrder) return;

    const receivedItems = receiptRows.filter(
      (item) => Number(item.quantity) > 0
    );

    if (receivedItems.length === 0) {
      toast.error(
        "Indica la cantidad recibida de al menos un producto."
      );
      return;
    }

    if (
      receivedItems.some(
        (item) =>
          item.quantity > item.pending_quantity ||
          item.unit_cost < 0
      )
    ) {
      toast.error(
        "La cantidad recibida no puede superar la pendiente."
      );
      return;
    }

    setSavingReceipt(true);

    try {
      await axios.post(
        `${API_URL}/purchase-orders/me/${selectedOrder.id}/receipts`,
        {
          received_at: receiptDate,
          notes: receiptNotes || null,
          items: receivedItems.map((item) => ({
            purchase_order_item_id:
              item.purchase_order_item_id,
            quantity: item.quantity,
            unit_cost: item.unit_cost,
            lot_number: item.lot_number || null,
            expiration_date:
              item.expiration_date || null,
          })),
        },
        headers()
      );

      toast.success(
        "Recepción registrada e inventario actualizado."
      );

      setSelectedOrder(null);
      await loadData();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "No fue posible registrar la recepción."
      );
    } finally {
      setSavingReceipt(false);
    }
  };

  const totals = useMemo(
    () =>
      items.reduce(
        (total, item) =>
          total +
          item.requested_quantity *
            item.estimated_unit_cost,
        0
      ),
    [items]
  );

  return (
    <div className="purchase-orders-page">
      <div className="purchase-orders-heading">
        <div>
          <p>Compras</p>
          <h1>Órdenes de compra</h1>
          <span>
            Planifica pedidos y recibe mercancía de forma parcial.
          </span>
        </div>

        <button type="button" onClick={loadData}>
          Actualizar
        </button>
      </div>

      {canManage && (
        <section className="purchase-orders-card">
          <h2>Nueva orden</h2>

          <form onSubmit={createOrder}>
            <div className="purchase-orders-form">
              <label>
                Proveedor
                <select
                  value={orderForm.supplier_id}
                  onChange={(event) =>
                    setOrderForm((current) => ({
                      ...current,
                      supplier_id: event.target.value,
                    }))
                  }
                  required
                >
                  <option value="">Selecciona un proveedor</option>
                  {suppliers.map((supplier) => (
                    <option
                      key={supplier.id}
                      value={supplier.id}
                    >
                      {supplier.name} — {supplier.nit}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Fecha de orden
                <input
                  type="date"
                  value={orderForm.order_date}
                  onChange={(event) =>
                    setOrderForm((current) => ({
                      ...current,
                      order_date: event.target.value,
                    }))
                  }
                  required
                />
              </label>

              <label>
                Fecha esperada
                <input
                  type="date"
                  value={orderForm.expected_date}
                  onChange={(event) =>
                    setOrderForm((current) => ({
                      ...current,
                      expected_date: event.target.value,
                    }))
                  }
                />
              </label>
            </div>

            <textarea
              value={orderForm.notes}
              onChange={(event) =>
                setOrderForm((current) => ({
                  ...current,
                  notes: event.target.value,
                }))
              }
              placeholder="Observaciones de la orden"
            />

            <div className="purchase-orders-picker">
              <select
                value={selectedProduct}
                onChange={(event) =>
                  setSelectedProduct(event.target.value)
                }
              >
                <option value="">Selecciona un producto</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.sku} — {product.name}
                  </option>
                ))}
              </select>

              <button type="button" onClick={addProduct}>
                Agregar producto
              </button>
            </div>

            <div className="purchase-orders-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Cantidad solicitada</th>
                    <th>Costo estimado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan="4">
                        Agrega productos a la orden.
                      </td>
                    </tr>
                  ) : (
                    items.map((item) => (
                      <tr key={item.product_id}>
                        <td>
                          <strong>{item.name}</strong>
                          <small>{item.sku}</small>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0.001"
                            step="0.001"
                            value={item.requested_quantity}
                            onChange={(event) =>
                              updateItem(
                                item.product_id,
                                "requested_quantity",
                                event.target.value
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.estimated_unit_cost}
                            onChange={(event) =>
                              updateItem(
                                item.product_id,
                                "estimated_unit_cost",
                                event.target.value
                              )
                            }
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() =>
                              removeItem(item.product_id)
                            }
                          >
                            Quitar
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="purchase-orders-total">
              Valor estimado: {totals.toLocaleString("es-CO")}
            </div>

            <button
              type="submit"
              disabled={saving || items.length === 0}
            >
              {saving ? "Guardando..." : "Crear orden"}
            </button>
          </form>
        </section>
      )}

      <section className="purchase-orders-card">
        <h2>Órdenes registradas</h2>

        <div className="purchase-orders-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Orden</th>
                <th>Proveedor</th>
                <th>Fecha</th>
                <th>Recepción</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6">Cargando órdenes...</td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="6">No hay órdenes registradas.</td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id}>
                    <td>{order.order_number}</td>
                    <td>{order.supplier_name}</td>
                    <td>{formatDate(order.order_date)}</td>
                    <td>
                      {formatQuantity(
                        order.total_received_quantity
                      )}{" "}
                      /{" "}
                      {formatQuantity(
                        order.total_requested_quantity
                      )}
                    </td>
                    <td>
                      <span
                        className={`purchase-order-status ${order.status}`}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td className="purchase-order-actions">
                      {canManage &&
                        order.status === "draft" && (
                          <button
                            type="button"
                            onClick={() =>
                              changeStatus(order, "sent")
                            }
                          >
                            Enviar
                          </button>
                        )}

                      {canManage &&
                        ["sent", "partial"].includes(
                          order.status
                        ) && (
                          <button
                            type="button"
                            onClick={() => openReceipt(order)}
                          >
                            Recibir
                          </button>
                        )}

                      {canManage &&
                        ["draft", "sent", "partial"].includes(
                          order.status
                        ) && (
                          <button
                            type="button"
                            onClick={() =>
                              changeStatus(order, "cancelled")
                            }
                          >
                            Cancelar
                          </button>
                        )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedOrder && (
        <div className="purchase-order-modal-backdrop">
          <section className="purchase-order-modal">
            <button
              type="button"
              className="purchase-order-close"
              onClick={() => setSelectedOrder(null)}
            >
              ×
            </button>

            <h2>
              Recepción de {selectedOrder.order_number}
            </h2>

            <form onSubmit={registerReceipt}>
              <div className="purchase-orders-form">
                <label>
                  Fecha de recepción
                  <input
                    type="date"
                    value={receiptDate}
                    onChange={(event) =>
                      setReceiptDate(event.target.value)
                    }
                    required
                  />
                </label>
              </div>

              <textarea
                value={receiptNotes}
                onChange={(event) =>
                  setReceiptNotes(event.target.value)
                }
                placeholder="Observaciones de la recepción"
              />

              {receiptRows.map((item, index) => (
                <div
                  className="purchase-receipt-item"
                  key={item.purchase_order_item_id}
                >
                  <strong>{item.product_name}</strong>
                  <small>
                    Pendiente:{" "}
                    {formatQuantity(item.pending_quantity)}
                  </small>

                  <input
                    type="number"
                    min="0"
                    max={item.pending_quantity}
                    step="0.001"
                    placeholder="Cantidad recibida"
                    value={item.quantity}
                    onChange={(event) =>
                      updateReceiptRow(
                        index,
                        "quantity",
                        event.target.value
                      )
                    }
                  />

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Costo unitario"
                    value={item.unit_cost}
                    onChange={(event) =>
                      updateReceiptRow(
                        index,
                        "unit_cost",
                        event.target.value
                      )
                    }
                  />

                  <input
                    type="text"
                    placeholder="Número de lote"
                    value={item.lot_number}
                    onChange={(event) =>
                      updateReceiptRow(
                        index,
                        "lot_number",
                        event.target.value
                      )
                    }
                  />

                  <input
                    type="date"
                    value={item.expiration_date}
                    onChange={(event) =>
                      updateReceiptRow(
                        index,
                        "expiration_date",
                        event.target.value
                      )
                    }
                  />
                </div>
              ))}

              <button
                type="submit"
                disabled={savingReceipt}
              >
                {savingReceipt
                  ? "Registrando..."
                  : "Registrar recepción"}
              </button>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}

export default CompanyPurchaseOrders;